import styles from './contact-us.module.scss';
import { Component, createRef, useId } from 'react';

import { FormControl, FormGroup } from '../../models/forms';
import { Logger } from '../../models/logger';
import { ApiConfig } from '../../services/api-config';
import { EnvironmentService } from '../../services/environment.service';
import { FileManagerService } from '../../services/file-manager.service';
import { HelperService } from '../../services/helper.service';
import { HttpProgressEvent, LightHttp } from '../../services/light-http.service';
import { lazyInject } from '../../context/module';
import FormInputComponent from '../ui/forms/form-input/form-input.component';
import ReCaptcha from '../ui/recaptcha/recaptcha.component';
import { CONTACT_US_COPY, fmtContactCopy } from './contact-us.copy';
import {
    ContactUsField,
    ContactUsFieldErrors,
    ContactUsOutcome,
    firstInvalidField,
    isUsableEmail,
    outcomeOfError,
    outcomeOfResponse,
    validateContactUs,
} from './contact-us.logic';

/** Test seam (same pattern as WebsiteCheckoutModal): the real services come from lazyInject. */
export interface ContactUsTestServices {
    http?: Pick<LightHttp, 'request'>;
    apiConfig?: Pick<ApiConfig, 'getSupportEmailEndpoint'>;
    helperService?: Pick<HelperService, 'getCommonHttpHeaders'>;
    fileManagerService?: Pick<FileManagerService, 'stringifyAllUploadedFileUploads'>;
    env?: Pick<EnvironmentService, 'domainname'>;
}

interface ContactUsProps {
    embedded?: boolean;
    __testServices?: ContactUsTestServices;
}

interface ContactUsFormProps extends ContactUsProps {
    /** Per-instance id prefix (React useId): /contact-us and the contact modal can be on screen together. */
    idBase: string;
}

interface ContactUsState {
    phase: 'idle' | 'sending' | 'sent';
    /** Upload progress of the request, integer 0-100. */
    uploaded: number;
    hasAttachment: boolean;
    errors: ContactUsFieldErrors;
    /** The refusal shown in the alert above the button; null when there is none. */
    failure: 'failed' | 'rate_limited' | null;
    sentWithEmail: boolean;
    /** Bumped by "Send another message" so the inputs, file picker and captcha mount fresh. */
    formKey: number;
}

/**
 * QA4-02 (prod fixes 2026-10-02 follow-up): what the contact MODAL's form held when it closed without sending. Escape,
 * the close key and a click outside all unmount the modal, and the typed message used to go with it; reopening the
 * modal now brings the email and message back. Only those two: a file input cannot be refilled from script and the
 * captcha token is single-use. Cleared once a message is sent. The /contact-us page keeps no draft (leaving it is a
 * navigation, not a dismissal). Module memory only: nothing is written to storage, so a reload forgets it.
 */
export interface ContactUsDraft {
    email: string;
    message: string;
}
let modalDraft: ContactUsDraft | null = null;

/** Tests only. */
export function __contactUsDraftForTests(): ContactUsDraft | null {
    return modalDraft;
}

/** Tests only. */
export function __resetContactUsDraftForTests() {
    modalDraft = null;
}

function newFormGroup(draft?: ContactUsDraft | null) {
    return new FormGroup({
        email: new FormControl(draft?.email || undefined),
        message: new FormControl(draft?.message || undefined),
        token: new FormControl(undefined),
    });
}

export class ContactUsForm extends Component<ContactUsFormProps, ContactUsState> {
    logger = Logger.getLogger('ContactUs');

    fileRef = createRef<HTMLInputElement>();
    emailRef = createRef<HTMLInputElement>();
    messageRef = createRef<HTMLTextAreaElement>();
    captchaRef = createRef<HTMLDivElement>();
    captchaWidgetRef = createRef<ReCaptcha>();
    alertRef = createRef<HTMLDivElement>();
    successHeadingRef = createRef<HTMLHeadingElement>();

    @lazyInject(EnvironmentService) private _env: EnvironmentService;
    @lazyInject(LightHttp) private _http: LightHttp;
    @lazyInject(ApiConfig) private _apiConfig: ApiConfig;
    @lazyInject(FileManagerService) private _fileManagerService: FileManagerService;
    @lazyInject(HelperService) private _helperService: HelperService;

    /** Set synchronously on the first press, so a double press sends one message (state updates are async). */
    private inFlight = false;

    /** QA4-02: the modal reopens with the draft it closed with (see [ContactUsDraft]). */
    private readonly initialDraft: ContactUsDraft | null = this.props.embedded ? modalDraft : null;

    formGroup = newFormGroup(this.initialDraft);

    readonly state: Readonly<ContactUsState> = {
        phase: 'idle',
        uploaded: 0,
        hasAttachment: false,
        errors: {},
        failure: null,
        sentWithEmail: false,
        formKey: 0,
    };

    private get http() {
        return this.props.__testServices?.http ?? this._http;
    }
    private get apiConfig() {
        return this.props.__testServices?.apiConfig ?? this._apiConfig;
    }
    private get helperService() {
        return this.props.__testServices?.helperService ?? this._helperService;
    }
    private get fileManagerService() {
        return this.props.__testServices?.fileManagerService ?? this._fileManagerService;
    }
    private get env() {
        return this.props.__testServices?.env ?? this._env;
    }

    componentWillUnmount() {
        if (!this.props.embedded) return;
        if (this.state.phase === 'sent') {
            modalDraft = null;
            return;
        }
        const email = (this.formGroup.value.email || '').toString();
        const message = (this.formGroup.value.message || '').toString();
        modalDraft = email.trim().length > 0 || message.trim().length > 0 ? { email, message } : null;
    }

    private id(part: string) {
        return `${this.props.idBase}-${part}`;
    }

    private get emailValue(): string {
        return (this.formGroup.value.email || '').trim();
    }

    private get files(): File[] {
        return Array.from(this.fileRef.current?.files || []);
    }

    handleCorrectCaptcha(token: string | null) {
        // patchValue: only the token changes, email and message are kept.
        this.formGroup.patchValue({ token });
        if (token && this.state.errors.captcha) this.clearError('captcha');
    }

    private clearError(field: ContactUsField) {
        if (!this.state.errors[field]) return;
        this.setState((s) => {
            const errors = { ...s.errors };
            delete errors[field];
            return { errors };
        });
    }

    private focusField(field: ContactUsField) {
        const target = {
            email: this.emailRef,
            message: this.messageRef,
            attachment: this.fileRef,
            captcha: this.captchaRef,
        }[field].current;
        target?.focus();
    }

    onSubmit(e?: { preventDefault?: () => void }) {
        e?.preventDefault?.();
        if (this.inFlight) return;
        const files = this.files;
        const errors = validateContactUs({
            email: this.emailValue,
            message: this.formGroup.value.message || '',
            token: this.formGroup.value.token,
            attachmentBytes: files.reduce((sum, f) => sum + f.size, 0),
        });
        const invalid = firstInvalidField(errors);
        if (invalid) {
            this.setState({ errors, failure: null }, () => this.focusField(invalid));
            return;
        }
        this.inFlight = true;
        this.setState({ phase: 'sending', uploaded: 0, hasAttachment: files.length > 0, errors: {}, failure: null });

        const formData = new FormData();
        formData.append('email', this.emailValue);
        let message = this.formGroup.value.message;
        message += '  File uploads: ' + this.fileManagerService.stringifyAllUploadedFileUploads();
        formData.append('message', message);
        for (const file of files) {
            formData.append('file', file);
        }
        formData.append('token', this.formGroup.value.token);
        const { response$, events$ } = this.http.request<{ error: boolean }>(
            this.apiConfig.getSupportEmailEndpoint(),
            'POST',
            formData,
            {
                headers: this.helperService.getCommonHttpHeaders(),
            },
        );
        events$.subscribe({
            next: (event: HttpProgressEvent) => {
                if (event.type === 'upload_progress' && event.event?.total > 0) {
                    const uploaded = Math.min(100, Math.floor((100 * event.event.loaded) / event.event.total));
                    if (uploaded !== this.state.uploaded) this.setState({ uploaded });
                }
            },
            error: () => undefined,
        });
        response$.subscribe({
            next: (result) => this.finish(outcomeOfResponse(result)),
            error: (err) => this.finish(outcomeOfError(err), err?.status),
        });
    }

    private finish(outcome: ContactUsOutcome, status?: number) {
        this.inFlight = false;
        if (outcome === 'sent') {
            if (this.props.embedded) modalDraft = null;
            this.setState(
                { phase: 'sent', sentWithEmail: this.emailValue.length > 0, failure: null },
                () => this.successHeadingRef.current?.focus(),
            );
            return;
        }
        // Never the server's text and never the address: the outcome and status are enough to find it in Graylog.
        this.logger.warn({ message: 'Contact form not sent', data: { outcome, status: status ?? 200 } });
        // The token is single-use and the server has seen it: the user must tick the box again. Text and file stay.
        this.captchaWidgetRef.current?.reset();
        this.formGroup.patchValue({ token: null });
        if (outcome === 'too_large') {
            this.setState(
                { phase: 'idle', uploaded: 0, failure: null, errors: { attachment: CONTACT_US_COPY['X4-12'] } },
                () => this.focusField('attachment'),
            );
            return;
        }
        this.setState(
            { phase: 'idle', uploaded: 0, failure: outcome === 'rate_limited' ? 'rate_limited' : 'failed' },
            () => this.alertRef.current?.focus(),
        );
    }

    sendAnother() {
        this.formGroup = newFormGroup();
        this.setState(
            (s) => ({
                phase: 'idle',
                uploaded: 0,
                hasAttachment: false,
                errors: {},
                failure: null,
                sentWithEmail: false,
                formKey: s.formKey + 1,
            }),
            () => this.emailRef.current?.focus(),
        );
    }

    render() {
        if (this.props.embedded) return this.renderForm();
        return (<div className={styles.wrapper}>
            <div className={styles['contact-card']}>
                <h2>Contact Us</h2>
                {this.renderForm()}
            </div>
        </div>)
    }

    private describedBy(...ids: (string | false | undefined)[]) {
        const joined = ids.filter(Boolean).join(' ');
        return joined.length > 0 ? joined : undefined;
    }

    private renderFieldError(field: ContactUsField) {
        const text = this.state.errors[field];
        if (!text) return null;
        return <div id={this.id(`${field}-error`)} className={styles['field-error']} data-jkey={`contact-error-${field}`}>{text}</div>;
    }

    renderSuccess() {
        return (<div className={styles['success-panel']} role="status" data-jkey="contact-success">
            <h3 ref={this.successHeadingRef} tabIndex={-1} className={styles['success-heading']}>{CONTACT_US_COPY['X4-6']}</h3>
            <p>{this.state.sentWithEmail ? CONTACT_US_COPY['X4-7'] : CONTACT_US_COPY['X4-8']}</p>
            <button type="button" className={styles['link-btn']} onClick={() => this.sendAnother()}>
                {CONTACT_US_COPY['X4-9']}
            </button>
        </div>);
    }

    renderForm() {
        const { errors, phase, failure } = this.state;
        const sending = phase === 'sending';
        return (<>
                {phase === 'sent' ? this.renderSuccess() : (
                <form key={this.state.formKey} className="contact-us-email-form" noValidate onSubmit={(e) => this.onSubmit(e)}>
                    <div className={styles['form-group']} onInput={() => {
                        // The email error goes as soon as the field is blank or looks like an address again.
                        if (this.emailValue.length === 0 || isUsableEmail(this.emailValue)) this.clearError('email');
                    }}>
                        <label htmlFor={this.id('email')}>Email</label>
                        <FormInputComponent
                            id={this.id('email')}
                            inputRef={this.emailRef}
                            control={this.formGroup.get('email')}
                            type="email"
                            autoComplete="email"
                            className={`form-control ${errors.email ? styles['is-invalid'] : ''}`}
                            placeholder="name@example.com"
                            aria-invalid={!!errors.email}
                            aria-describedby={this.describedBy(errors.email && this.id('email-error'), this.id('email-help'))}
                        ></FormInputComponent>
                        {this.renderFieldError('email')}
                        <div id={this.id('email-help')} className={styles['helper-text']}>
                            Email is optional, but required if you'd like a response.
                        </div>
                    </div>
                    <div className={styles['form-group']}>
                        <label htmlFor={this.id('message')}>Message (required)</label>
                        <textarea
                            id={this.id('message')}
                            ref={this.messageRef}
                            className={`${styles['form-control']} ${errors.message ? styles['is-invalid'] : ''}`}
                            rows={4}
                            defaultValue={this.state.formKey === 0 ? this.initialDraft?.message || undefined : undefined}
                            aria-required="true"
                            aria-invalid={errors.message ? true : undefined}
                            aria-describedby={this.describedBy(errors.message && this.id('message-error'))}
                            onInput={(e: any) => {
                                this.formGroup.patchValue({ 'message': e.target.value });
                                if (e.target.value.trim().length > 0) this.clearError('message');
                            }}
                        ></textarea>
                        {this.renderFieldError('message')}
                    </div>
                    <div className={styles['form-group']}>
                        <label htmlFor={this.id('file')}>Attachment (Optional)</label>
                        <input
                            ref={this.fileRef}
                            type="file"
                            className={`${styles['form-control-file']} ${errors.attachment ? styles['is-invalid'] : ''}`}
                            id={this.id('file')}
                            aria-invalid={errors.attachment ? true : undefined}
                            aria-describedby={this.describedBy(errors.attachment && this.id('attachment-error'), this.id('file-help'))}
                            onChange={() => this.clearError('attachment')}
                        />
                        {this.renderFieldError('attachment')}
                        <div id={this.id('file-help')} className={styles['helper-text']}>
                            Attaching the relevant file helps us diagnose technical issues faster.
                        </div>
                    </div>
                    <div className={styles['form-group']}>
                        <div
                            ref={this.captchaRef}
                            role="group"
                            aria-label="Verification"
                            tabIndex={-1}
                            className={styles['captcha']}
                            aria-describedby={this.describedBy(errors.captcha && this.id('captcha-error'))}
                        >
                            <ReCaptcha ref={this.captchaWidgetRef} onChange={e => this.handleCorrectCaptcha(e)}></ReCaptcha>
                        </div>
                        {this.renderFieldError('captcha')}
                    </div>

                    {failure && <div
                        ref={this.alertRef}
                        tabIndex={-1}
                        role="alert"
                        className={`${styles.alert} ${styles.error}`}
                        data-jkey="contact-failure"
                    >
                        {failure === 'rate_limited' ? CONTACT_US_COPY['X4-11'] : CONTACT_US_COPY['X4-10']}
                    </div>}

                    <button type="submit" className={styles['submit-btn']} disabled={sending} aria-busy={sending || undefined}>
                        {sending ? CONTACT_US_COPY['X4-4'] : 'Send Message'}
                    </button>

                    {sending && this.state.hasAttachment && <div
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={this.state.uploaded}
                        aria-labelledby={this.id('progress')}
                        className={styles['upload-progress']}
                    >
                        <span id={this.id('progress')}>{fmtContactCopy('X4-5', { n: this.state.uploaded })}</span>
                    </div>}

                </form>)}

                <div className={styles['contact-info']}>
                    Or email us at support@{this.env.domainname}
                </div>
        </>)
    }
}

/** The contact form on /contact-us and in the contact modal (same component, own ids per instance). */
export default function ContactUs(props: ContactUsProps) {
    const idBase = useId();
    return <ContactUsForm {...props} idBase={`contact-us${idBase}`} />;
}
